import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SidebarIcon from "./SidebarIcon";
import { sidebarIcons } from "./sidebarIcons";
import { signOut } from "firebase/auth";

import { auth } from "../../Models/firebaseConfig";
import { Routes } from "../../Routes";
import { useUser } from "../User/UserObject";
import { useMenuContext } from "./MenuContext";

import clsx from "clsx";

import styles from "./SidebarUserFooter.module.css";

type SidebarUserFooterProps = {
  onNavigate?: () => void;
};

function SidebarUserFooter({ onNavigate }: SidebarUserFooterProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { sidebarCompact } = useMenuContext();

  const displayName =
    [user?.Firstname, user?.Lastname].filter(Boolean).join(" ").trim() ||
    user?.Email ||
    t("menu.account");

  const onLogout = useCallback(() => {
    signOut(auth)
      .then(() => {
        navigate(Routes.Home);
        onNavigate?.();
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
      });
  }, [navigate, onNavigate]);

  if (sidebarCompact) {
    return (
      <div className={clsx(styles.footer, styles.footerCompact)}>
        <Link
          to={Routes.Account}
          className={styles.compactAccount}
          onClick={onNavigate}
          title={t("menu.account")}
        >
          <span className={styles.avatar} aria-hidden="true">
            <SidebarIcon icon={sidebarIcons.user} muted />
          </span>
        </Link>
        <button
          type="button"
          className={styles.compactLogout}
          onClick={onLogout}
          title={t("account.logout")}
          aria-label={t("account.logout")}
        >
          <SidebarIcon icon={sidebarIcons.logout} muted />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.footer}>
      <div className={styles.accountRow}>
        <Link
          to={Routes.Account}
          className={styles.accountLink}
          onClick={onNavigate}
        >
          <span className={styles.avatar} aria-hidden="true">
            <SidebarIcon icon={sidebarIcons.user} muted />
          </span>
          <span className={styles.accountText}>
            <span className={styles.accountName}>{displayName}</span>
            <span className={styles.accountHint}>{t("menu.account")}</span>
          </span>
        </Link>
        <button
          type="button"
          className={styles.logoutIconButton}
          onClick={onLogout}
          aria-label={t("account.logout")}
        >
          <SidebarIcon icon={sidebarIcons.logout} muted />
        </button>
      </div>
    </div>
  );
}

export default SidebarUserFooter;
